<template>
  <div class="edit-view display-in-center view" v-loading.fullscreen="loading || !isReadyToScan">
    <div class="step-label">
      {{ $t('pair') }}
    </div>
    <div class="barcode-scanner">
      <div id="scan-area">
        <div class="scan-selection-camera" @click="openDialog" v-show="cameras.length > 0">
          <IconSwitch />
        </div>
        <div id="video-area">
          <video playsinline autoplay id="video"></video>
        </div>
      </div>

      <BarcodeSymbologiesSelection
        class="absolute bottom-2 z-50 left-2 flex w-52"
        @update:onSymbologiesChange="setBarcodeSymbologies"
        v-model:symbologies="symbologies"
      />

      <ScanArea />
    </div>
    <div class="barcode-number-block">
      <span>
        {{ $t('Number of Scanned Barcodes') }}
      </span>
      <span class="label-box" @click="onCheckScannedBarcode">
        {{ scannedBarcodeList.length }}
      </span>
    </div>
    <div v-if="estimateChildCount > 0" class="barcode-number-block">
      <span>
        {{ $t('Number of reserved children barcode') }}
      </span>
      <span class="label-box">
        {{ estimateChildCount }}
      </span>
    </div>
    <el-button
      circle
      :disabled="scannedBarcodeList.length === 0"
      type="primary"
      class="submit-button"
      @click="submit()"
    >
      {{ $t('OK') }}
    </el-button>
    <SocketMobileScanComponent v-model:loading="loading" :on-scan="onSocketMobileScan" />
  </div>
  <el-dialog
    :lock-scroll="true"
    center
    v-model="dialogVisible"
    width="90%"
    top="0"
    :show-close="false"
    :destroy-on-close="false"
    :title="checkScannedBarcode ? $t('Scanned Barcodes') : ''"
  >
    <template v-if="checkScannedBarcode">
      <p v-if="scannedBarcodeList.length === 0">
        {{ $t('no barcodes') }}
      </p>
      <BarcodeLabelList v-else :barcodes="scannedBarcodeList || []" />
    </template>
    <template #footer>
      <span class="dialog-footer">
        <el-button type="primary" @click="onCloseDialog">
          {{ $t('OK') }}
        </el-button>
      </span>
    </template>
  </el-dialog>
  <SelectionCameraPopup v-model:selectedCamera="selectedCamera" :cameras="cameras" v-model:isShow="isOpenDialog" />
</template>

<script lang="ts">
import BarcodeLabelList from '@/components/BarcodeLabelList.vue'
import BarcodeSymbologiesSelection from '@/components/BarcodeSymbologiesSelection.vue'
import SelectionCameraPopup from '@/components/dialog/SelectionCameraPopup.vue'
import MultipleBarcodeScanMixin from '@/components/mixins/MultipleBarcodeScanMixin.vue'
import IconSwitch from '@/components/svg/IconSwitch.vue'
import ScanArea from '@/components/svg/ScanArea.vue'
import { CLEAR_ERROR, PAIRING, SAVE_SCANNED_BARCODES } from '@/store/actions'
import { getBarcode, getChildren, getEstimateBarcodeChildren, pairBarcode } from '@/utils/api'
import { QRCODE_BARCODE_TYPE_REGEX } from '@/utils/constants'
import errorHandler from '@/utils/errorHandler'
import { displayTargetAlert, openMessage } from '@/utils/helpers'
import { IBarcode, IBarcodeDefinitionType, IProject, TError } from 'smartbarcode-web-core/src/utils/types/index'
import { Options, mixins } from 'vue-class-component'
import { NavigationGuardNext, RouteLocationNormalized } from 'vue-router'

@Options({
  components: {
    ScanArea,
    BarcodeLabelList,
    BarcodeSymbologiesSelection,
    IconSwitch,
    SelectionCameraPopup,
  },
  name: 'Pairscan',
})
export default class PairScan extends mixins(MultipleBarcodeScanMixin) {
  loading = false
  dialogVisible = false
  checkScannedBarcode = false
  children = [] as IBarcode[]
  estimateChildCount = 0
  extParams = {} as Record<string, unknown>
  isReadyToScan = true

  async created() {
    try {
      this.isForcedHideMultipleScan = true
      this.loading = true
      this.children = await getChildren(this.$route.params.barcodeId)
      const { count } = await getEstimateBarcodeChildren(this.$route.params.barcodeId)
      this.estimateChildCount = count
    } catch (error) {
      errorHandler(error as TError)
    } finally {
      this.loading = false
    }
  }

  async beforeRouteLeave(to: RouteLocationNormalized, from: RouteLocationNormalized, next: NavigationGuardNext) {
    await this.onLeaveGuard(to, from, next)
  }

  onCheckScannedBarcode() {
    this.checkScannedBarcode = true
    this.dialogVisible = true
  }

  onCloseDialog() {
    this.dialogVisible = false
    this.checkScannedBarcode = false
  }

  barcodeTypeInfo(barcodeType: string): Record<string, IBarcodeDefinitionType> {
    return this.$store.state.project?.details?.barcodeTypes[barcodeType]
  }

  get isAllowForcePairing() {
    return this.$store.state.project?.details?.allowForcePairing || false
  }

  isBarcodeInChildrenList(barcode: IBarcode): boolean {
    return !!this.children.find((bc) => bc.id === barcode.id)
  }

  async validateBarcode(resultScanned: IBarcode, listScannedResult?: IBarcode[]) {
    const currentProjectId = this.$store.state.project.details.id
    const scannedBCProjectId = resultScanned.projectId
    if (currentProjectId !== scannedBCProjectId) {
      throw this.$t('scanned_barcode_not_same_project')
    }

    const { barcodeType, hasParent } = resultScanned

    if (this.parentBarcodeId === resultScanned?.id) throw this.$t('barcode_pairing_by_itself_error')

    if (!barcodeType) {
      throw this.$t('barcode not activated')
    }

    if (this.isBarcodeInChildrenList(resultScanned)) {
      throw this.$t('scanned_barcode_in_children_list')
    }

    if (hasParent && !this.isAllowForcePairing) {
      throw this.$t('barcode_already_paired')
    }

    const barcodeTypeItem = this.barcodeTypeInfo(barcodeType)
    if (!barcodeTypeItem?.allowToBePaired || resultScanned.isDeactivated || !resultScanned.isReadyToAddTrackData) {
      throw this.$t('barcode_can_not_be_paired')
    }

    if (
      resultScanned?.reservedParentIds?.length > 0 &&
      !resultScanned?.reservedParentIds?.includes(this.parentBarcodeId)
    ) {
      throw this.$t('errors.3046')
    }

    const validScannedBarcodes = (listScannedResult || []).filter((result) => !result.isExternal)
    const listScannedId = Array.from(new Set([resultScanned?.id, ...validScannedBarcodes.map((result) => result?.id)]))

    const data = {
      parentBarcodeId: this.parentBarcodeId,
      childrenBarcodeIds: listScannedId,
      isDryRun: true,
    }
    try {
      await pairBarcode(data)
    } catch (error) {
      if (error === '3044' && this.isAllowForcePairing) {
        await this.$confirm('', this.$t('confirm_overwrite_parent'), {
          confirmButtonText: this.$t('ok'),
          confirmButtonClass: 'danger',
          cancelButtonText: this.$t('cancel'),
        })
          .then(async () => {
            const payload = {
              ...data,
              forcePairing: true,
            }
            await pairBarcode(payload)
            this.extParams = {
              ...this.extParams,
              forcePairing: true,
            }
          })
          .catch((err: unknown) => {
            throw err
          })
          .finally(() => this.$store.commit(CLEAR_ERROR))
        return
      }
      throw error
    }
  }

  get parentBarcodeId() {
    return this.$store.state.barcode?.barcode?.id || ''
  }

  forward() {
    this.$store.dispatch(SAVE_SCANNED_BARCODES, [...this.barcodes])
    this.$router.push({
      name: 'scanned-barcode-list',
      params: {
        title: this.$t('pairing_read_result'),
        operation: PAIRING,
        extParams: JSON.stringify(this.extParams),
      },
    })
  }

  get project(): IProject {
    return this.$store.state.project.details
  }

  get hasExternalIdInAnyBarcodeType(): boolean {
    const barcodeTypes = this.project?.barcodeTypes
    if (!barcodeTypes) return false

    return Object.values(barcodeTypes).some((barcodeType) => {
      return barcodeType?.activationFields?.externalId?.isAvailable === true
    })
  }

  private resolveBarcodeQuery(plainText: string) {
    if (plainText.match(QRCODE_BARCODE_TYPE_REGEX)) {
      const barcodeId = plainText.split('/').pop() || ''
      if (
        barcodeId === this.currentBarcodeId ||
        this.scannedBarcodeList.find((val: IBarcode) => val.id === barcodeId)
      ) {
        openMessage(this.$t('barcode_already_scanned', { id: barcodeId }), 'info')
        return null
      }
      return { barcodeId }
    }
    const projectCode = this.$route?.params?.project.split('@')
    if (projectCode.length > 1) projectCode.pop()
    return { barcodeId: `projectCode=${projectCode[0]}`, externalId: plainText || undefined }
  }

  private async fetchBarcodeResult(barcodeId: string, externalId?: string): Promise<IBarcode | null> {
    const barcodeLookupKey = this.buildBarcodeLookupKey(barcodeId, externalId)
    this.pendingRequestBarcodeIds.add(barcodeLookupKey)
    try {
      return await getBarcode(barcodeId, externalId)
    } catch {
      return null
    } finally {
      this.pendingRequestBarcodeIds.delete(barcodeLookupKey)
    }
  }

  private addExternalBarcode(plainText: string) {
    const externalBarcode = ({ id: plainText, externalId: plainText, isExternal: true } as unknown) as IBarcode
    if (!this.scannedBarcodeList.find((bc: IBarcode) => bc.id === externalBarcode.id)) {
      this.scannedBarcodeList.push(externalBarcode)
      this.onNewScannedBarcode(externalBarcode)
      openMessage(this.$t('barcode detected'), 'success')
      this.valueCode = ''
    }
  }

  private async addFoundBarcode(resultScanned: IBarcode) {
    if (
      this.bulkUpdateOperationLimitCount &&
      this.scannedBarcodeList.length >= this.bulkUpdateOperationLimitCount.maxValue
    ) {
      throw this.$t('bulk_tracking_over_barcodes_is_not_allowed', {
        count: this.bulkUpdateOperationLimitCount.maxValue,
      })
    }

    await this.validateBarcode(resultScanned, this.scannedBarcodeList)
    await displayTargetAlert(resultScanned)

    this.scannedBarcode = resultScanned.id
    if (typeof this.addBarcode === 'function') {
      this.addBarcode(resultScanned.id)
    }

    if (!this.scannedBarcodeList.find((bc: IBarcode) => bc.id === resultScanned.id)) {
      this.scannedBarcodeList.push(resultScanned)
      this.onNewScannedBarcode(resultScanned)
      openMessage(this.$t('barcode detected'), 'success')
      this.valueCode = ''
    }
  }

  async handleGotScanBarcode(plainText: string) {
    const scannedText = plainText.trim()
    if (!scannedText || !this.componentMounted) {
      return
    }
    try {
      const query = this.resolveBarcodeQuery(scannedText)
      if (!query) return

      if (this.scannedBarcodeList.find((barcode: IBarcode) => barcode.id === scannedText)) {
        openMessage(this.$t('barcode_already_scanned', { id: scannedText }), 'info')
        return
      }

      const { barcodeId, externalId } = query
      const barcodeLookupKey = this.buildBarcodeLookupKey(barcodeId, externalId)
      if (this.pendingRequestBarcodeIds.has(barcodeLookupKey)) {
        return
      }

      const resultScanned = await this.fetchBarcodeResult(barcodeId, externalId)
      if (!this.componentMounted) return

      if (!resultScanned) {
        if (!this.hasExternalIdInAnyBarcodeType) {
          throw this.$t('no_barcode_type_allows_external_id')
        }
        this.addExternalBarcode(scannedText)
        return
      }

      await this.addFoundBarcode(resultScanned)
    } catch (error) {
      this.handleScanError(error)
    } finally {
      this.valueCode = ''
    }
  }
}
</script>

<style lang="scss" scoped>
@import './src/assets/css/mixins.scss';

.edit-view.view {
  padding-left: 0;
  padding-right: 0;
}

.step-label {
  margin-bottom: 25px;
}

.barcode-scanner {
  height: 430px;
  background: rgb(32, 32, 32);
  position: relative;
}

.barcode-scanner video {
  width: 100%;
  height: 100%;
}
.scan-selection-camera {
  position: absolute;
  top: 0.5em;
  left: 0.5em;
}
</style>
